
import { defineComponent, ref, onMounted, provide, computed } from 'vue';
import { useRoute, onBeforeRouteLeave } from 'vue-router';
import { api } from '@/services/Api';
import { OrderDetails, OrderProductDetails, OrderProductCustomerDetails, Customer, Order } from '@/models/Order';
import useOrder from '@/modules/useOrder';
import swal from 'sweetalert2';
import router from '@/router';
import Q from 'q';
import { ArrivalConfirmation, CouponDiscountType, CouponLimitationType, OrderStatus, OrderStatusColors } from '@/models/Enums';
import moment from 'moment';
import OrderActions from '@/components/Modals/OrderActions.vue';
import QuoteActions from '@/components/Modals/QuoteActions.vue';
import { generalStore } from '@/store';
import OrderArrivalConfirmationActions from '@/components/Order/OrderArrivalConfirmationActions.vue';
import { useI18n } from 'vue-i18n';
import {
    PriceType,
    IdText,
    CustomerToChangeDate,
    BussinesClientByNewOrderObject,
    AssignEmployeeRequest,
    SendContractFormRequest,
    IdName
} from '@/models/Interfaces';
import mixin from '@/mixin';
import _ from 'lodash';
import OrderHeader from '@/components/Order/OrderHeader.vue';
import { Coupon } from '@/models/Provider';
import OrderDataView from '@/components/Order/OrderDataView.vue';
import OrderMobileActionsFooter from '@/components/Order/OrderMobileActionsFooter.vue';
import InputNumber from 'primevue/inputnumber';
import { UpdateOrderProductCustomerRequest, CalculateForUpdateProductCustomer, UpdateQuoteProductCustomerRequest } from '@/models/Interfaces';
import { createDebounce } from '@/helper';
import useOrderCustomers from '@/modules/useOrderCustomers';
import OverlayModal from '../Calendar/OverlayModal.vue';
import ContractFormModal from './ContractForm/ContractFormModal.vue';
import { CalendarStopSale } from '@/models/Calendar';
import AddTagsModal from '@/components/Modals/AddTagsModal.vue';
import TagsManagementModal from '@/components/Modals/TagsManagementModal.vue';
import CrmCustomerChatMessagePopup from '@/components/CrmCustomers/CrmCustomerChatMessagePopup.vue';

export default defineComponent({
    components: {
        OrderArrivalConfirmationActions,
        OrderActions,
        QuoteActions,
        OrderHeader,
        OrderDataView,
        OrderMobileActionsFooter,
        InputNumber,
        OverlayModal,
        ContractFormModal,
        AddTagsModal,
        TagsManagementModal,
        CrmCustomerChatMessagePopup
    },
    name: 'Order',
    props: {
        id: {
            type: String,
            default: ''
        },
        isNew: {
            type: Boolean,
            default: false
        },
        crmCustomerId: {
            type: String,
            default: ''
        },
        crmCustomerLeadId: {
            type: String,
            default: ''
        }
    },
    beforeRouteEnter(to, from, next) {
        if (from.name == 'modal-order-edit-remarks') {
            to.params.needScrollBottom = '1';
        }
        next();
    },
    async setup(props) {
        let chatTimerId = 0;
        onBeforeRouteLeave(async () => {
            if (!props.isNew) {
                clearTimeout(chatTimerId);
            }
            return true;
        });
	    const isQuote = computed(() => (props.crmCustomerLeadId || router.currentRoute.value.fullPath.startsWith('/quote/') ? true : false));
        const orderTags = ref<IdName[]>([]);
        const tagsLoaded = ref(false);
        const clients = ref<BussinesClientByNewOrderObject[]>([]);
        const creatingBlocked = ref(false);
        const loaded = ref(false);
        const { t } = useI18n();
        const { removemillis, getMinutes, saveStoreOrder, apiCalculateOrder, apiSaveOrder, goToSelectProduct, apiSaveQuote } = useOrder();
        const route = useRoute();
        const order = ref<OrderDetails>(new OrderDetails());
        const { CalculateCustomers } = useOrderCustomers(order);
        const channels = ref<IdText[]>([]);
        const msgSent = ref(false);
        const messagedMarked = ref(false);
        const message = ref('');
        const isActionsExpand = ref(false);
        const isQuoteActionsExpand = ref(false);
        const actionsMenu = ref('');
        const filterType = ref<string>('ALL');
        const priceTypes = ref<PriceType[]>([]);
        const priceTypeSelect = ref();
        const businessClientSelect = ref();
        const incorrectMobile = ref(false);
        const incorrectEmail = ref(false);
        const canChangeUnitPrice = generalStore.getters.canChangeUnitPrice;
        const modalCrmCustomerChatMessage = ref<InstanceType<typeof CrmCustomerChatMessagePopup>>();
        const addTagsModal = ref<InstanceType<typeof AddTagsModal>>();
        const tagsManagementModal = ref<InstanceType<typeof TagsManagementModal>>();
        const orderProductExist = computed<boolean>(() => order.value.products.length > 0);
        const discountChangedManually = ref(false);
        let externalCouponConfirmationOpen = false;
        const isGroupPrice = computed(() => {
            return mixin.methods.isGroupPrice(order.value.priceTypeId, priceTypes.value);
        });
        const orderType = computed(() => {
            return order.value.priceType.name + ', ' + order.value.channel?.name;
        });
        const showContractFormModal = ref(false);

        async function markMessagesAsRead() {
            if (order.value.messages && order.value.messages.length > 0 && messagedMarked.value == false) {
                const lastMessage = order.value.messages[0];
                const response = await api.markOrderMessageAsRead(props.id, lastMessage.id);
                if (response.error || response.errorMessage) {
                    swal.fire({
                        icon: 'error',
                        text: response.errorMessage
                    });
                    return;
                }
                messagedMarked.value = true;
            }
        }

        let getNextMessages: (showOld?: boolean) => void = () => {
            'funtion template for stupid composition api';
        };
        async function sendMsg() {
            msgSent.value = true;
            if (!message.value.trim()) {
                msgSent.value = false;
                return;
            }
            if (msgSent.value) {
                swal.showLoading();
                const apiResult = await api.SendOrderMessage(props.id, message.value);
                if (apiResult.data?.error) {
                    swal.fire({
                        icon: 'error',
                        text: apiResult.data?.error
                    });
                    return;
                }
                message.value = '';
                await getNextMessages();
                msgSent.value = false;

                const textarea = document.getElementById('textMessage');
                if (textarea) {
                    textarea.style.height = 'initial';
                }
                swal.close();
            }
        }
        provide('isActionsExpand', isActionsExpand);
        provide('isQuoteActionsExpand', isQuoteActionsExpand);

        async function getMessages(showOld = false, fromId?: string, showLoading = false) {
            if (showLoading) swal.showLoading();
            const chatResult = await api.GetOrderMessages(props.id, filterType.value, fromId, showOld);
            if (chatResult.data) {
                messagedMarked.value = false;
                if (!fromId) {
                    order.value.messages = chatResult.data.messages;
                } else if (showOld) {
                    order.value.messages.push(...chatResult.data.messages);
                } else {
                    order.value.messages.unshift(...chatResult.data.messages);
                }
            }
            if (showLoading) swal.close();

            if (!showOld) {
                clearTimeout(chatTimerId);
                chatTimerId = setTimeout(getNextMessages, 30 * 1000);
            }
        }

        getNextMessages = async (showOld = false) => {
            let fromId: string | undefined = undefined;

            if (order.value.messages.length > 0)
                fromId = showOld ? order.value.messages[order.value.messages.length - 1].id : order.value.messages[0].id;
            await getMessages(showOld, fromId);
        };

        function changeChatType(type: string) {
            filterType.value = type;
            getMessages(false, undefined, true);
        }

        function formatChatDate(date: string) {
            const d = moment(date);
            return moment().isSame(d, 'day') ? d.format('HH:mm:ss') : d.format('DD/MM/YYYY HH:mm');
        }

        function EditOrder() {
            router.push({
                name: 'modal-order-edit',
                params: { orderId: props.id }
            });
        }

        swal.close();

        function actions() {
            isActionsExpand.value = true;
            if (window.innerWidth < 991) {
                actionsMenu.value = 'mobile';
                return;
            }
            actionsMenu.value = 'desktop';
        }

        function changeRemarks() {
            if (isQuote.value) {
                router.push({ name: 'modal-quote-edit-remarks', params: { id: props.isNew ? '0' : props.id, crmCustomerLeadId: order.value.crmCustomerLeadId! } });
            } else {
                router.push({ name: 'modal-order-edit-remarks', params: { id: props.isNew ? '0' : props.id } });
            }
        }

        async function changeOrderProductDateExistOrder(customer: OrderProductCustomerDetails, productId: string) {
            // validate if the product date has passed
            swal.showLoading();
            const apiPromise = api.orderQuoteProductDateValidateToChange({
                orderId: isQuote.value ? null : props.id,
                quoteId: isQuote.value ? props.id : null,
                productId: customer.orderProductId,
                customerId: customer.id
            });
            await Q.delay(400);
            const response = await apiPromise;

            if (response.errorMessage) {
                if (response.errorCode == 944) {
                    const swalResult = await swal.fire({
                        icon: 'info',
                        text: t('alert.orderChange.dataPassedConfirm'),
                        customClass: {
                            confirmButton: 'save-button-wrapper popup-bookit-button my-1 px-5',
                            cancelButton: 'close-button-wrapper popup-bookit-button my-1 px-5'
                        },
                        buttonsStyling: false,
                        showCancelButton: true,
                        confirmButtonText: t('button.yes'),
                        cancelButtonText: t('editProductPage.cancelEdit.alertCancel')
                    });
                    if (!swalResult.isConfirmed) {
                        return;
                    }
                } else {
                    swal.fire({ icon: 'error', text: response.errorMessage });
                    return;
                }
            }
            swal.close();
            const storeOrder = new OrderDetails();
            storeOrder.id = order.value.id;
            storeOrder.priceTypeId = order.value.priceTypeId;
            generalStore.commit('setOrder', storeOrder);
            router.push({
                name: isQuote.value ? 'modal-quote-edit-productdate' : 'modal-order-edit-productdate',
                params: {
                    id: props.id,
                    productId: productId,
                    orderProductId: customer.orderProductId,
                    customerId: customer.id,
                    slotId: customer.slot.id,
                    date: customer.date.substr(0, customer.date.length - 2)
                }
            });
        }
        async function changeOrderProductDate(customer: OrderProductCustomerDetails, productId: string) {
            if (!customer.editMode) {
                return;
            }
            if (props.isNew) {
                const req: CustomerToChangeDate = {
                    productId: productId,
                    customerId: customer.id,
                    date: customer.date,
                    priceTypeId: order.value.priceTypeId,
                    slot: customer.slot,
                    couponId: customer.couponId
                };
                generalStore.commit('setCustomerToChange', req);
                isQuote.value
                    ? router.push({ name: 'modal-newquote-edit-productdate' })
                    : router.push({ name: 'modal-neworder-edit-productdate' });
            } else {
                await changeOrderProductDateExistOrder(customer, productId);
            }
        }

        function changeStatus(status: number) {
            order.value.statusId = status;
            order.value.status = OrderStatus[status];
        }

        const isArrivalConfirmationExpand = ref(false);

        function arrivalConfirmation() {
            isArrivalConfirmationExpand.value = true;
            if (window.innerWidth < 991) {
                actionsMenu.value = 'mobile';
                return;
            }
            actionsMenu.value = 'desktop';
        }

        function showQuoteActions() {
            isQuoteActionsExpand.value = true;
            if (window.innerWidth < 991) {
                actionsMenu.value = 'mobile';
                return;
            }
            actionsMenu.value = 'desktop';
        }

        async function onSaveArrivalConfirmation(value: ArrivalConfirmation) {
            isArrivalConfirmationExpand.value = false;
            swal.showLoading();
            const response = await api.UpdateArrivalConfirmation({ orderId: order.value.id, arrivalConfirmation: value });
            if (response.errorMessage) {
                swal.fire({ icon: 'error', text: response.errorMessage });
                return;
            }
            order.value.arrivalConfirmationId = value;
            swal.close();
        }
        function updateOrderPrice(newOrder: OrderDetails) {
            order.value.commissionPercent = newOrder.commissionPercent;
            order.value.totalDiscountAmount = newOrder.totalDiscountAmount;
            order.value.priceTotal = newOrder.priceTotal;
            order.value.totalToPay = newOrder.totalToPay;
            order.value.gross = newOrder.gross;
            order.value.commissionTotal = newOrder.commissionTotal;
            order.value.net = newOrder.net;
            order.value.paid = newOrder.paid;
            order.value.ballance = newOrder.ballance;
            order.value.discountPercent = newOrder.discountPercent;
            order.value.businessClientTotalAmount = newOrder.businessClientTotalAmount;
        }
        function findCustomer(order: OrderDetails, orderProductId: string, customerId: string) {
            for (const product of order.products) {
                for (const customer of product.customers) {
                    if (customer.orderProductId === orderProductId && customer.id === customerId) {
                        return customer;
                    }
                }
            }
            return null;
        }
        async function updateOrderProductCustomer(newOrder: OrderDetails, customer: OrderProductCustomerDetails) {
            updateOrderPrice(newOrder);
            const customerToUpdate = findCustomer(newOrder, customer.orderProductId, customer.id);
            if (customerToUpdate) {
                order.value.products.forEach(orderProduct => {
                    const foundCustomer = orderProduct.customers.find(
                        x => x.orderProductId == customer.orderProductId && x.id == customer.id
                    );
                    if (foundCustomer != null) {
                        foundCustomer.priceForOne = customerToUpdate.priceForOne;
                        foundCustomer.discountAmount = customerToUpdate.discountAmount;
                        foundCustomer.quantity = customerToUpdate.quantity;
                        foundCustomer.price = customerToUpdate.quantity * customerToUpdate.priceForOne - customerToUpdate.discountAmount;
                        foundCustomer.editMode = false;
                        foundCustomer.externalCouponEditConfirmed = false;
                        orderProduct.price = orderProduct.customers.reduce((accumulator, object) => {
                            return accumulator + object.price;
                        }, 0);
                    }
                });
            }
        }
        async function saveOrderCustomerExistOrder(customer: OrderProductCustomerDetails) {
            swal.showLoading();
            const req: UpdateOrderProductCustomerRequest = {
                orderId: order.value.id,
                orderProductid: customer.orderProductId,
                orderProductCustomerId: customer.id,
                unitPrice: customer.editUnitPrice,
                discount: customer.editDiscountAmount,
                quantity: customer.editQuantity,
                slotId: customer.slot?.id ?? customer.slotId,
                discountChangedManually: discountChangedManually.value,
                productCustomerTypeId: customer.customerTypeId,
                priceListId: customer.priceListId,
                qtyToUse: customer.qtyToUse,
                externalCouponEditConfirmed: customer.externalCouponEditConfirmed,
                ignoreQty: false
            };
            const apiPromise = api.UpdateOrderProductCustomer(req);
            await Q.delay(400);
            const response = await apiPromise;
            if (response.errorMessage && response.errorCode == 900) {
                const swalResult = await swal.fire({
                    icon: 'info',
                    text: response.errorMessage,
                    customClass: {
                        confirmButton: 'save-button-wrapper popup-bookit-button my-1 px-5',
                        cancelButton: 'close-button-wrapper popup-bookit-button my-1 px-5'
                    },
                    buttonsStyling: false,
                    showCancelButton: true,
                    confirmButtonText: t('editProductPage.cancelEdit.alertConfirm'),
                    cancelButtonText: t('editProductPage.cancelEdit.alertCancel')
                });
                if (!swalResult.isConfirmed) {
                    return;
                }
                await Q.delay(150);
                swal.showLoading();
                req.ignoreQty = true;
                const apiPromise2 = api.UpdateOrderProductCustomer(req);
                await Q.delay(400);
                const response2 = await apiPromise2;
                if (response2.errorMessage) {
                    swal.fire({ icon: 'error', text: response.errorMessage });
                    return;
                }
                if (response2.data) {
                    updateOrderProductCustomer(response2.data.order, customer);
                }
                swal.fire({
                    position: 'center',
                    icon: 'success',
                    title: t('alert.order-updated'),
                    showConfirmButton: false,
                    timer: 1500
                });
                return;
            }
            if (response.errorMessage) {
                swal.fire({ icon: 'error', text: response.errorMessage });
                return;
            }
            if (response.data) {
                updateOrderProductCustomer(response.data.order, customer);
            }
            swal.fire({
                position: 'center',
                icon: 'success',
                title: t('alert.order-updated'),
                showConfirmButton: false,
                timer: 1500
            });
        }
        async function saveOrderCustomerExistQuote(customer: OrderProductCustomerDetails) {
            swal.showLoading();
            const req: UpdateQuoteProductCustomerRequest = {
                orderProductCustomerId: customer.id,
                unitPrice: customer.editUnitPrice,
                discount: customer.editDiscountAmount,
                quantity: customer.editQuantity,
                slotId: customer.slot?.id ?? customer.slotId,
                discountChangedManually: discountChangedManually.value,
                productCustomerTypeId: customer.customerTypeId,
                priceListId: customer.priceListId,
                ignoreQty: false
            };
            const apiPromise = api.UpdateQuoteProductCustomer(req);
            await Q.delay(400);
            const response = await apiPromise;
            if (response.errorMessage && response.errorCode == 900) {
                const swalResult = await swal.fire({
                    icon: 'info',
                    text: response.errorMessage,
                    customClass: {
                        confirmButton: 'save-button-wrapper popup-bookit-button my-1 px-5',
                        cancelButton: 'close-button-wrapper popup-bookit-button my-1 px-5'
                    },
                    buttonsStyling: false,
                    showCancelButton: true,
                    confirmButtonText: t('editProductPage.cancelEdit.alertConfirm'),
                    cancelButtonText: t('editProductPage.cancelEdit.alertCancel')
                });
                if (!swalResult.isConfirmed) {
                    return;
                }
                await Q.delay(150);
                swal.showLoading();
                req.ignoreQty = true;
                const apiPromise2 = api.UpdateQuoteProductCustomer(req);
                await Q.delay(400);
                const response2 = await apiPromise2;
                if (response2.errorMessage) {
                    swal.fire({ icon: 'error', text: response.errorMessage });
                    return;
                }
                if (response2.data) {
                    updateOrderProductCustomer(response2.data.quote, customer);
                }
                swal.fire({
                    position: 'center',
                    icon: 'success',
                    title: t('alert.order-updated'),
                    showConfirmButton: false,
                    timer: 1500
                });
                return;
            }
            if (response.errorMessage) {
                swal.fire({ icon: 'error', text: response.errorMessage });
                return;
            }
            if (response.data) {
                updateOrderProductCustomer(response.data.quote, customer);
            }
            swal.fire({
                position: 'center',
                icon: 'success',
                title: t('alert.order-updated'),
                showConfirmButton: false,
                timer: 1500
            });
        }
        async function saveOrderCustomerExistEntityHandler(customer: OrderProductCustomerDetails) {
            if (isQuote.value) {
                await saveOrderCustomerExistQuote(customer);
            } else {
                await saveOrderCustomerExistOrder(customer);
            }
        }
        async function saveOrderCustomerNewOrder(customer: OrderProductCustomerDetails, productId: string) {
            const orderReq = _.cloneDeep(order.value);
            const productItem = orderReq.products.find(x => x.productId == productId);
            if (!productItem) {
                return;
            }
            const customerItem = productItem.customers.find(x => x.date == customer.date && x.id == customer.id);
            if (!customerItem) {
                return;
            }
            customerItem.priceForOne = customer.editUnitPrice;
            customerItem.discountAmount = customer.editDiscountAmount;
            customerItem.quantity = customer.editQuantity;
            swal.showLoading();
            const result = await apiCalculateOrder(orderReq);
            if (!result) {
                return;
            }
            const productResp = result.products.find(x => x.productId == productId);
            if (!productResp) {
                return;
            }
            const customerResp = productResp.customers.find(x => x.date == customer.date && x.id == customer.id);
            if (!customerResp) {
                return;
            }
            const basketrProduct = order.value.products.find(p => p.productId == productId);
            if (!basketrProduct) {
                return;
            }
            let orderCoupon: Coupon = new Coupon();
            if (customer.couponId) {
                orderCoupon = order.value.coupons.find(x => x.id == customer.couponId) ?? new Coupon();
                if (orderCoupon == undefined) {
                    return;
                }
            }
            order.value.commissionPercent = result.commissionPercent;
            order.value.totalDiscountAmount = result.discountAmount;
            order.value.priceTotal = result.priceTotal;
            order.value.totalToPay = result.totalToPay;
            order.value.gross = result.gross;
            order.value.commissionTotal = result.commissionTotal;
            order.value.net = result.net;
            order.value.paid = result.paid;
            order.value.ballance = result.ballance;
            order.value.discountPercent = result.discountPercent;

            if (customer.couponId) {
                const increasedQty = customerResp.discountAmount > customer.discountAmount;
                const differenceNumbers = Math.abs(customerResp.quantity - customer.quantity);
                const adjustment = increasedQty ? -1 : 1;

                if (orderCoupon.limitationType === CouponLimitationType.CustomerQuantity) {
                    orderCoupon.leftLimitationTotal += adjustment * differenceNumbers;
                } else {
                    let adjustmentValue = 0;

                    if (orderCoupon.discountPercent) {
                        adjustmentValue = ((differenceNumbers * customer.priceForOne) / 100) * orderCoupon.discountPercent;
                    } else if (orderCoupon.discountAmount) {
                        adjustmentValue = differenceNumbers * Number(orderCoupon.discountAmount);
                    } else if (orderCoupon.ticketFixedUnitPrice) {
                        adjustmentValue = differenceNumbers * Number(orderCoupon.ticketFixedUnitPrice);
                    } else if (orderCoupon.discountType == CouponDiscountType.NoDiscount) {
                        adjustmentValue = differenceNumbers * Number(customer.priceForOne);
                    }

                    orderCoupon.leftLimitationTotal += adjustment * adjustmentValue;
                }
            }
            customer.priceForOne = customerResp.priceForOne;
            customer.discountAmount = customerResp.discountAmount;
            customer.quantity = customerResp.quantity;
            customer.price = customerResp.price;
            basketrProduct.price = productResp.price;
            customer.editMode = false;
            swal.close();
        }
        async function saveOrderCustomer(customer: OrderProductCustomerDetails, productId: string) {
            if (
                customer.editDiscountAmount == customer.discountAmount &&
                customer.editQuantity == customer.quantity &&
                customer.editUnitPrice == customer.priceForOne
            ) {
                customer.editMode = false;
                return;
            }
            if (props.isNew) {
                await saveOrderCustomerNewOrder(customer, productId);
            } else {
                await saveOrderCustomerExistEntityHandler(customer);
            }
        }
        function updateEntityAfterDeleteCustomer(newData: OrderDetails, entityProductId: string, customerId: string) {
            order.value = newData;
            updateOrderPrice(newData);
            order.value.products.forEach((product, index) => {
                const findIndex = product.customers.findIndex(x => x.orderProductId == entityProductId && x.id == customerId);
                const existCustomer = findIndex >= 0;
                if (existCustomer && product.customers.length == 1) {
                    order.value.products.splice(index, 1);
                } else if (existCustomer) {
                    product.customers.splice(findIndex, 1);
                }
                if (existCustomer) {
                    product.price = product.customers.reduce((accumulator, object) => {
                        return accumulator + object.price;
                    }, 0);
                }
            });
        }
        async function deleteOrderProductCustomer(entityProductId: string, customerId: string) {
            const apiResponse = await api.deleteOrderProductCustomer(props.id, entityProductId, customerId);
            if (apiResponse.errorMessage) {
                swal.fire({ icon: 'error', text: apiResponse.errorMessage });
                return false;
            }
            if (!apiResponse.data) {
                return false;
            }
            updateEntityAfterDeleteCustomer(apiResponse.data.order, entityProductId, customerId);
            return true;
        }
        async function deleteQuoteProductCustomer(entityProductId: string, customerId: string) {
            const apiResponse = await api.deleteQuoteProductCustomer(props.id, entityProductId, customerId);
            if (apiResponse.errorMessage) {
                swal.fire({ icon: 'error', text: apiResponse.errorMessage });
                return false;
            }
            if (!apiResponse.data) {
                return false;
            }
            updateEntityAfterDeleteCustomer(apiResponse.data.quote, entityProductId, customerId);
            return true;
        }
        async function removeProductCustomerInExistEntity(entityProductId: string, customerId: string) {
            swal.showLoading();
            let result = false;
            if (isQuote.value) {
                result = await deleteQuoteProductCustomer(entityProductId, customerId);
            } else {
                result = await deleteOrderProductCustomer(entityProductId, customerId);
            }
            if (!result) {
                return;
            }
            swal.fire({
                position: 'center',
                icon: 'success',
                title: isQuote.value ? t('alert.quote.updated') : t('alert.order.updated'),
                showConfirmButton: false,
                timer: 1500
            });
        }
        async function calculateOrder() {
            swal.showLoading();
            const result = await apiCalculateOrder(order.value);
            if (result) {
                order.value.currencyId = result.currency;
                order.value.currencySymbol = result.currencySign;
                order.value.commissionPercent = result.commissionPercent;
                order.value.totalDiscountAmount = result.discountAmount;
                order.value.discountPercent = result.discountPercent;
                order.value.priceTotal = result.priceTotal;
                order.value.totalToPay = result.totalToPay;
                order.value.gross = result.gross;
                order.value.commissionTotal = result.commissionTotal;
                order.value.net = result.net;
                order.value.paid = result.paid;
                order.value.ballance = result.ballance;
                order.value.products = result.products;
                order.value.businessClientTotalAmount = result.businessClientTotalAmount;
            }
            swal.close();
        }
        async function removeProductCustomerInNewOrder(customer: OrderProductCustomerDetails, productId: string, customerIndex: number) {
            if (!props.isNew && order.value.products.length == 1 && order.value.products[0].customers.length == 1) {
                swal.fire({ icon: 'error', text: t('alert.order.delete-last-item') });
                return;
            }
            const productIndex = order.value.products.findIndex(x => x.productId == productId);
            if (productIndex < 0) {
                return;
            }
            if (customer.couponId) {
                const orderCoupon = order.value.coupons.find(x => x.id == customer.couponId) ?? new Coupon();
                if (orderCoupon.limitationType == CouponLimitationType.CustomerQuantity) {
                    orderCoupon.leftLimitationTotal += customer.quantity;
                } else {
                    if (orderCoupon.discountPercent) {
                        orderCoupon.leftLimitationTotal += ((customer.quantity * customer.priceForOne) / 100) * orderCoupon.discountPercent;
                    } else if (orderCoupon.discountAmount) {
                        orderCoupon.leftLimitationTotal += customer.quantity * Number(orderCoupon.discountAmount);
                    } else if (orderCoupon.ticketFixedUnitPrice) {
                        orderCoupon.leftLimitationTotal += customer.quantity * Number(orderCoupon.ticketFixedUnitPrice);
                    } else if (orderCoupon.discountType == CouponDiscountType.NoDiscount) {
                        orderCoupon.leftLimitationTotal += customer.quantity * Number(customer.priceForOne);
                    }
                }
            }
            if (order.value.products[productIndex].customers.length == 1) {
                order.value.products.splice(productIndex, 1);
            } else {
                order.value.products[productIndex].customers.splice(customerIndex, 1);
            }

            await calculateOrder();
        }
        async function removeProductCustomer(customer: OrderProductCustomerDetails, productId: string, customerIndex: number) {
            const swalResult = await swal.fire({
                icon: 'info',
                title: isQuote.value ? t('confirm.delete.quoteProductCustomer') : t('confirm.delete.orderProductCustomer'),
                customClass: {
                    confirmButton: 'save-button-wrapper popup-bookit-button my-1 px-5',
                    cancelButton: 'close-button-wrapper popup-bookit-button my-1 px-5'
                },
                buttonsStyling: false,
                showCancelButton: true,
                confirmButtonText: t('editProductPage.cancelEdit.alertConfirm'),
                cancelButtonText: t('editProductPage.cancelEdit.alertCancel')
            });
            if (!swalResult.isConfirmed) {
                return;
            }
            await Q.delay(200);
            if (props.isNew) {
                await removeProductCustomerInNewOrder(customer, productId, customerIndex);
            } else {
                await removeProductCustomerInExistEntity(customer.orderProductId, customer.id);
            }
        }

        async function customerEdit(customer: OrderProductCustomerDetails) {
            if (externalCouponConfirmationOpen) {
                return;
            }
            customer.externalCouponEditConfirmed = false;
            if (!props.isNew && !isQuote.value && customer.couponId && order.value.hasExternalCoupon) {
                externalCouponConfirmationOpen = true;
                const result = await swal.fire({
                    icon: 'warning',
                    text: t('alert.order.external-coupon-edit-confirmation'),
                    customClass: {
                        confirmButton: 'save-button-wrapper popup-bookit-button my-1 px-5',
                        cancelButton: 'close-button-wrapper popup-bookit-button my-1 px-5'
                    },
                    buttonsStyling: false,
                    showCancelButton: true,
                    confirmButtonText: t('general.word.yes'),
                    cancelButtonText: t('button.cancel')
                });
                externalCouponConfirmationOpen = false;
                if (!result.isConfirmed) {
                    return;
                }
                customer.externalCouponEditConfirmed = true;
            }
            customer.editDiscountAmount = customer.discountAmount;
            customer.editQuantity = customer.quantity;
            customer.editUnitPrice = customer.priceForOne;
            customer.editTotalAmount = customer.price;
            customer.editMode = true;
        }

        async function addProduct() {
            const orderInStore = new OrderDetails();
            if (props.isNew) {
                if (!order.value.priceTypeId) {
                    return;
                }
            } else {
                orderInStore.id = order.value.id;
                orderInStore.currencyId = order.value.currencyId;
                orderInStore.priceTypeId = order.value.priceType.id;
                orderInStore.businessClientId = order.value.businessClientId;
                if (isQuote.value) {
                    orderInStore.crmCustomerLeadId = order.value.crmCustomerLeadId;
                }
                await saveStoreOrder(orderInStore);
            }
            goToSelectProduct(order.value.crmCustomerLeadId ? order.value.crmCustomerLeadId : null);
        }

        async function saveOrder() {
            swal.showLoading();
            return await apiSaveOrder(order.value, message => {
                if (message?.includes('Invalid Mobile')) {
                    incorrectMobile.value = true;
                }
                if (message?.includes('Invalid CustomerEmail')) {
                    incorrectEmail.value = true;
                }
                return true;
            });
        }

        async function saveQuote() {
            swal.showLoading();
            return await apiSaveQuote(order.value, message => {
                if (message?.includes('Invalid Mobile')) {
                    incorrectMobile.value = true;
                }
                if (message?.includes('Invalid CustomerEmail')) {
                    incorrectEmail.value = true;
                }
                return true;
            });
        }

        async function saveOrderHandler() {
            const newOrder = await saveOrder();
            if (newOrder?.error) {
                swal.fire({
                    icon: 'success',
                    title: newOrder?.error
                });
            }
            if (newOrder && newOrder.orderId) {
                await swal.fire({
                    icon: 'success',
                    title: t('alert.orderWasCreated'),
                    confirmButtonText: t('alert.goToOrder')
                });
                generalStore.commit('clearOrder');
                router.push({ name: 'order', params: { id: newOrder.orderId } });
            }
        }

        async function saveQuoteHandler() {
            const newOrder = await saveQuote();
            if (newOrder?.error) {
                swal.fire({
                    icon: 'success',
                    title: newOrder?.error
                });
            }
            if (newOrder && newOrder.quoteId) {
                await swal.fire({
                    icon: 'success',
                    title: t('alert.quoteWasCreated'),
                    confirmButtonText: t('alert.goToQuote')
                });
                generalStore.commit('clearOrder');
                router.push({ name: 'quote-details', params: { id: newOrder.quoteId } });
            }
        }

        const submitForm = async (event: any) => {
            event.target.classList.add('was-validated');
            if (event.target.checkValidity() === false) {
                event.preventDefault();
                event.stopPropagation();
                swal.fire({
                    icon: 'warning',
                    title: t('alert.mandatoryfields'),
                    confirmButtonText: t('button.close')
                });
                return;
            }
            if (creatingBlocked.value) {
                return;
            }
            creatingBlocked.value = true;
            if (isQuote.value) {
                await saveQuoteHandler();
            } else {
                await saveOrderHandler();
            }
            creatingBlocked.value = false;
        };

        async function back() {
            router.push({ name: 'orders' });
        }

        function checkHeader() {
            const result = document?.getElementById('order-details-title-client');
            const height = result?.offsetHeight;
            if (height && height > 80) {
                const title = document?.getElementById('order-details-title');
                title?.classList.add('client-name-not-fit');
            }
            const result2 = document?.getElementById('order-details-title-client');
            const height2 = result2?.offsetHeight;
            if (!height2) {
                return;
            }
            if (height2 > 110) {
                const title = document?.getElementById('order-details-title');
                title?.classList.remove('client-name-not-fit');
                title?.classList.add('client-name-not-fit2');
            }
        }
        function mobileNumberChanged() {
            incorrectMobile.value = false;
        }
        function emailChanged() {
            incorrectEmail.value = false;
        }
        async function getQuoteDetails() {
            const apiResult = await api.GetQuote(props.id);
            if (apiResult.errorMessage || !apiResult.data) {
                swal.fire({
                    icon: 'error',
                    text: apiResult.errorMessage || 'No data'
                });
                return false;
            }
            order.value = apiResult.data?.order || new OrderDetails();
            return true;
        }
        async function getOrderDetails() {
            const apiResult = await api.GetOrder(props.id);
            const windowViewPortScrolLY = ref(window.scrollY);
            await Q.delay(400);
            if (apiResult.data?.error) {
                swal.fire({
                    icon: 'error',
                    text: apiResult.data?.error
                });
                return false;
            }
            order.value = apiResult.data?.order || new OrderDetails();
            getMessages();
            // const input = document.getElementById('textMessage');
            // input?.addEventListener('keyup', function(event) {
            //     if (event.key === 'Enter') {
            //         event.preventDefault();
            //         sendMsg();
            //     }
            // });
            checkHeader();
            if (route.params.needScrollBottom) {
                const container = document.getElementById('main-content');
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
            }
            const callback = (entries: any, observer: any) => {
                entries.forEach((entry: any) => {
                    markMessagesAsRead();
                });
            };
            const observer = new IntersectionObserver(callback, {
                threshold: [0.5] // If 50% of the element is in the screen, we count it!
                // Can change the thresholds based on your needs. The default is 0 - it'll run only when the element first comes into view
            });
            const div = document.getElementById('order-chat');
            if (div) {
                observer.observe(div);
            }
            return true;
        }

        async function onMountedAction() {
            swal.showLoading();
            if (props.isNew) {
                order.value = generalStore.getters.getNewOrder;
                if (!order.value) {
                    order.value = new OrderDetails();
                    order.value.newproduct = new OrderProductDetails();
                    generalStore.commit('setOrder', order.value);
                    const direct = channels.value.find(x => x.text.toLowerCase() == 'direct');
                    if (direct) {
                        order.value.channelId = direct.id;
                    }
                }
                if (props.crmCustomerId) {
                    order.value.crmCustomerId = props.crmCustomerId;
                }
                if (props.crmCustomerLeadId) {
                    order.value.crmCustomerLeadId = props.crmCustomerLeadId;
                }
                if (route.params.loadCrmCustomerInfo) {
                    const apiResult = await api.GetCrmCustomerForNewQuote(props.crmCustomerLeadId);
                    await Q.delay(400);
                    if (apiResult.errorMessage || !apiResult.data) {
                        swal.fire({
                            icon: 'error',
                            text: apiResult.errorMessage || 'No data'
                        });
                        return;
                    }
                    const customer = new Customer();
                    order.value.businessClientId = apiResult.data.businessClientId;
                    customer.firstName = apiResult.data.firstName;
                    customer.lastName = apiResult.data.lastName;
                    customer.mobile = apiResult.data.mobile;
                    customer.email = apiResult.data.email;
                    order.value.leadCustomer = customer;
                }
                channels.value = await generalStore.dispatch('getChannels');
                priceTypes.value = await generalStore.dispatch('getPriceTypes');
                const clientResult = await api.GetBussinesClientsByNewOrder();
                clients.value = clientResult.data?.businessClients || [];
                const direct = channels.value.find(x => x.text.toLowerCase() == 'direct');
                if (direct) {
                    order.value.channelId = direct.id;
                }
                priceTypeSelect.value = {
                    value: 0,
                    placeholder: '',
                    valueProp: 'id',
                    trackBy: 'text',
                    label: 'text',
                    required: true,
                    searchable: true,
                    options: priceTypes.value
                };
                businessClientSelect.value = {
                    placeholder: '',
                    valueProp: 'id',
                    trackBy: 'text',
                    label: 'name',
                    searchable: true,
                    options: clients.value
                };
                if (order.value.priceTypeId && order.value.products.length > 0) {
                    await calculateOrder();
                } else {
                    swal.close();
                }
            } else {
                let success = true;
                if (isQuote.value) {
                    success = await getQuoteDetails();
                } else {
                    success = await getOrderDetails();
                }
                if (!success) {
                    return;
                }
                swal.close();
            }
            loaded.value = true;
        }

        const adjustTextareaHeight = () => {
            const textarea = document.getElementById('textMessage');
            if (textarea) {
                textarea.style.height = 'auto';
                textarea.style.height = `${textarea.scrollHeight}px`;
            }
        };

        const getFullNameOrderStatus = (status: string) => {
            switch (status) {
                case 'OK':
                    return 'order.title.full-name-confirmed';
                case 'RQ':
                    return 'order.title.full-name-requested';
                default:
                    return 'order.title.full-name-canceled';
            }
        };

        function assignEmployee(req: AssignEmployeeRequest) {
            const employeeId = req.employeeId;
            order.value.employeeIds.push(employeeId);
        }
        function unassignEmployee(req: AssignEmployeeRequest) {
            const employeeId = req.employeeId;
            order.value.employeeIds = order.value.employeeIds.filter(x => x != employeeId);
        }
        function goBack() {
            router.go(-1);
        }
        function onBussinessClientChange(businessClientId: number | null) {
            if (businessClientId == null) {
                order.value.leadCustomer.firstName = '';
                order.value.leadCustomer.lastName = '';
                order.value.leadCustomer.email = '';
                order.value.leadCustomer.mobile = '';
            } else {
                const client = clients.value.find(x => x.id == businessClientId);
                if (!client) {
                    return;
                }
                order.value.leadCustomer.firstName = client.contactFirstName;
                order.value.leadCustomer.lastName = client.contactLastName;
                order.value.leadCustomer.email = client.email;
                order.value.leadCustomer.mobile = client.mobile;
            }
        }

        function goToCrmCustomerDetails() {
            if (order.value.crmCustomerId) {
                router.push({
                    name: 'crm-customer-details',
                    params: { id: order.value.crmCustomerId }
                });
            }
        }

        function goToCrmCustomerLeadDetails() {
            router.push({
                name: 'crm-customer-lead-details',
                params: { crmCustomerId: order.value.crmCustomerId!, crmCustomerLeadId: order.value.crmCustomerLeadId! }
            });
        }

        async function calculateOrderCustomerRow(
            customer: OrderProductCustomerDetails,
            productId: string,
            discountChangedManuallyParam = false
        ) {
            if (!customer.editDiscountAmount && !customer.editQuantity && !customer.editUnitPrice) {
                return;
            }
            discountChangedManually.value = discountChangedManuallyParam;
            swal.showLoading();
            const customerTypeId = props.isNew ? customer.id : customer.customerTypeId;
            const orderProductCustomerId = props.isNew ? null : customer.id;
            const req: CalculateForUpdateProductCustomer = {
                priceTypeId: order.value.priceTypeId,
                productId: productId,
                orderProductCustomerId: orderProductCustomerId,
                productCustomerTypeId: customerTypeId,
                unitPrice: customer.editUnitPrice,
                discount: customer.editDiscountAmount,
                quantity: customer.editQuantity,
                qtyToUse: customer.qtyToUse,
                slotId: customer.slot?.id ?? customer.slotId,
                discountChangedManually: discountChangedManually.value,
                startDateTime: customer.date,
                couponId: customer.couponId,
                priceListId: customer.priceListId,
                externalCouponEditConfirmed: customer.externalCouponEditConfirmed,
                ignoreQty: false
            };
            const response = await api.CalculateForUpdateOrderProductCustomer(req);
            if (response.errorMessage) {
                swal.fire({
                    icon: 'error',
                    text: response.errorMessage
                });
                return;
            }
            swal.close();
            if (!response.data) {
                return;
            }
            customer.editDiscountAmount = response.data.discount;
            customer.editQuantity = response.data.quantity;
            customer.editUnitPrice = response.data.unitPrice;
            customer.editTotalAmount = response.data.totalAmount;
        }

        async function calculateQuoteCustomerRow(
            customer: OrderProductCustomerDetails,
            productId: string,
            discountChangedManuallyParam = false
        ) {
            if (!customer.editDiscountAmount && !customer.editQuantity && !customer.editUnitPrice) {
                return;
            }
            discountChangedManually.value = discountChangedManuallyParam;
            swal.showLoading();
            const customerTypeId = props.isNew ? customer.id : customer.customerTypeId;
            const orderProductCustomerId = props.isNew ? null : customer.id;
            const req: CalculateForUpdateProductCustomer = {
                priceTypeId: order.value.priceTypeId,
                productId: productId,
                orderProductCustomerId: orderProductCustomerId,
                productCustomerTypeId: customerTypeId,
                unitPrice: customer.editUnitPrice,
                discount: customer.editDiscountAmount,
                quantity: customer.editQuantity,
                qtyToUse: customer.qtyToUse,
                slotId: customer.slot?.id ?? customer.slotId,
                discountChangedManually: discountChangedManually.value,
                startDateTime: customer.date,
                couponId: customer.couponId,
                priceListId: customer.priceListId,
                ignoreQty: false
            };
            const response = await api.CalculateForUpdateQuoteProductCustomer(req);
            if (response.errorMessage) {
                swal.fire({
                    icon: 'error',
                    text: response.errorMessage
                });
                return;
            }
            swal.close();
            if (!response.data) {
                return;
            }
            customer.editDiscountAmount = response.data.discount;
            customer.editQuantity = response.data.quantity;
            customer.editUnitPrice = response.data.unitPrice;
            customer.editTotalAmount = response.data.totalAmount;
        }

        async function calculateCustomerRowHandler(
            customer: OrderProductCustomerDetails,
            productId: string,
            discountChangedManuallyParam = false
        ) {
            if (isQuote.value) {
                await calculateQuoteCustomerRow(customer, productId, discountChangedManuallyParam);
            } else {
                await calculateOrderCustomerRow(customer, productId, discountChangedManuallyParam);
            }
        }

        function updateContractFormModalVisibility(newValue: boolean) {
            showContractFormModal.value = newValue;
        }

        async function deleteQuote() {
            const swalResult = await swal.fire({
                icon: 'warning',
                text: t('alert.delete-quote', [order.value.leadCustomer.fullName]),
                customClass: {
                    confirmButton: 'save-button-wrapper popup-bookit-button my-1 px-5',
                    cancelButton: 'close-button-wrapper popup-bookit-button my-1 px-5'
                },
                buttonsStyling: false,
                showCancelButton: true,
                confirmButtonText: t('button.yes'),
                cancelButtonText: t('editProductPage.cancelEdit.alertCancel')
            });
            if (!swalResult.isConfirmed) {
                return;
            }
            await Q.delay(200);
            swal.showLoading();
            const response = await api.deleteQuote(props.id);
            if (response.errorMessage) {
                swal.fire({
                    icon: 'error',
                    text: response.errorMessage
                });
                return;
            }
            router.push({ name: 'crm-customer-lead-quotes' });
        }
	
	async function removeTagFromOrder(tag: IdName) {
            if (!props.isNew) {
                const swalResult = await swal.fire({
                    icon: 'info',
                    title: t('alert.removeTagFromOrder.confirm'),
                    customClass: {
                        confirmButton: 'save-button-wrapper popup-bookit-button my-1 px-5',
                        cancelButton: 'close-button-wrapper popup-bookit-button my-1 px-5'
                    },
                    buttonsStyling: false,
                    showCancelButton: true,
                    confirmButtonText: t('alert.yes'),
                    cancelButtonText: t('alert.no')
                });
                if (!swalResult.isConfirmed) {
                    return;
                }
                await Q.delay(200);
                swal.showLoading();
                const response = await api.removeTagFromOrder(props.id, tag.id);
                if (response.errorMessage) {
                    swal.fire({
                        icon: 'error',
                        text: response.errorMessage
                    });
                    return;
                }
                swal.fire({
                    position: 'center',
                    icon: 'success',
                    showConfirmButton: false,
                    timer: 1500
                });
            }
            const tagIndex = order.value.tags.findIndex(x => x.id == tag.id);
            if (tagIndex >= 0) {
                order.value.tags.splice(tagIndex, 1);
            }
        }

        async function shareQuote() {
            router.push({ name: 'sharequote', params: { quoteId: props.id } });
        }

        function openCrmCustomerChatMessageModal() {
            if (!order.value.crmCustomerId) {
                return;
            }
            modalCrmCustomerChatMessage.value?.open(order.value.crmCustomerId, order.value.leadCustomer?.fullName);
        }

        async function createOrderFromQuote() {
            const toStore = _.cloneDeep(order.value);
            toStore.crmCustomerLeadId = null;
            toStore.quoteId = order.value.id;
            toStore.id = '';
            const products: OrderProductDetails[] = [];
            order.value.products.forEach(product => {
                const productItem: OrderProductDetails = {
                    arrivalInstructions: '',
                    covidInfo: '',
                    calendarDate: '',
                    currencyId: '',
                    currencySign: '',
                    productName: '',
                    slotId: '',
                    startTime: '',
                    discountTotal: 0,
                    businessClientTotalToPay: 0,
                    customerTotalToPay: 0,
                    price: 0,
                    priceTotalWithOutDiscount: 0,
                    notAvailableCustomerTypeIds: [],
                    isAvailable: true,
                    date: product.date,
                    dateTime: product.dateTime,
                    duration: product.duration,
                    productId: product.productId,
                    slot: product.customers[0].slot,
                    customers: product.customers.map(x => {
                        return {
                            couponId: x.couponId,
                            couponName: x.couponName,
                            date: x.date,
                            id: x.customerTypeId,
                            slotId: x.slotId,
                            price: x.price,
                            priceForOne: x.priceForOne,
                            discountAmount: x.discountAmount,
                            name: x.name,
                            priceWithoutDiscount: x.priceWithoutDiscount,
                            quantity: x.quantity
                        } as OrderProductCustomerDetails;
                    })
                };
                products.push(productItem);
            });
            toStore.products = products;
            await saveStoreOrder(toStore);
            router.push({ name: 'neworder' });
        }

        function onSelectQuoteAction(action: string) {
            switch (action) {
                case 'delete-quote': {
                    deleteQuote();
                    return;
                }
                case 'share': {
                    shareQuote();
                    return;
                }
                case 'create-order': {
                    createOrderFromQuote();
                    return;
                }
            }
        }

        async function contractFormModalConfirmed(req: SendContractFormRequest) {
            swal.showLoading();

            req.orderId = order.value.id;
            const response = await api.SendContractFormToClient(req);

            if (response.errorMessage) {
                swal.fire({
                    icon: 'error',
                    text: response.errorMessage
                });
                return;
            } else {
                swal.fire({
                    icon: 'success',
                    text: t('contractForm.sendCustomerContractFormEmail.success')
                });
                return;
            }
        }
	
	async function openTagsManagementModal() {
            if (!tagsLoaded.value) {
                swal.showLoading();
                const apiResponse = await api.getOrderTags();
                if (apiResponse.errorMessage || !apiResponse.data) {
                    swal.fire({
                        icon: 'error',
                        text: apiResponse.errorMessage || 'No data'
                    });
                    return;
                }
                orderTags.value = apiResponse.data.tags;
                swal.close();
            }
            tagsLoaded.value = true;
            tagsManagementModal.value?.open();
        }
        async function onDeleteTag(tagId: string) {
            swal.showLoading();
            const existTag = orderTags.value.find(t => t.id == tagId);
            if (!existTag) {
                return;
            }
            const result = await swal.fire({
                icon: 'warning',
                title: t('order.alert.delete-tag'),
                customClass: {
                    confirmButton: 'save-button-wrapper popup-bookit-button my-1 px-5',
                    denyButton: 'close-button-wrapper popup-bookit-button my-1 px-5'
                },
                buttonsStyling: false,
                showDenyButton: true,
                showCloseButton: true,
                confirmButtonText: t('alert.yesDeleteIt'),
                denyButtonText: t('button.cancel')
            });
            if (!result.isConfirmed) {
                return;
            }
            await Q.delay(200);
            swal.showLoading();
            const apiResponse = await api.deleteOrderTag(tagId);
            if (apiResponse.errorMessage || !apiResponse.data) {
                swal.fire({
                    icon: 'error',
                    text: apiResponse.errorMessage || 'No data'
                });
                return;
            }
            orderTags.value.splice(orderTags.value.indexOf(existTag), 1);
            swal.close();
        }
        async function openAddTagsModal() {
            if (!tagsLoaded.value) {
                swal.showLoading();
                const apiResponse = await api.getOrderTags();
                if (apiResponse.errorMessage || !apiResponse.data) {
                    swal.fire({
                        icon: 'error',
                        text: apiResponse.errorMessage || 'No data'
                    });
                    return;
                }
                orderTags.value = apiResponse.data.tags;
                swal.close();
            }
            tagsLoaded.value = true;
            const selectedOrderTagIds = order.value.tags.map(x => x.id);
            addTagsModal.value?.open(selectedOrderTagIds);
        }
        function updateOrderTagName(tag: IdName) {
            const orderTag = order.value.tags.find(x => x.id == tag.id);
            if (orderTag) {
                orderTag.name = tag.name;
            }
        }
        async function onSaveTag(tag: IdName) {
            if (!tagsManagementModal.value) {
                return;
            }
            swal.showLoading();
            const apiResponse = await api.saveOrderTag(tag);
            if (apiResponse.errorMessage || !apiResponse.data) {
                swal.fire({
                    icon: 'error',
                    text: apiResponse.errorMessage || 'No data'
                });
                return;
            }
            const apiTag = apiResponse.data;
            const existTag = orderTags.value.find(x => x.id == apiTag.id);
            if (existTag) {
                existTag.name = apiTag.name;
                tagsManagementModal.value.editingTag = null;
                updateOrderTagName(tag);
            } else {
                orderTags.value.unshift({ id: apiTag.id, name: apiTag.name });
                tagsManagementModal.value.newTagName = '';
            }
            swal.close();
        }
        async function onSaveTagsHandler(selectedTagIds: string[]) {
            swal.showLoading();
            if (!props.isNew) {
                const apiResponse = await api.updateOrderTags(props.id, selectedTagIds);
                if (apiResponse.errorMessage || !apiResponse.data) {
                    swal.fire({
                        icon: 'error',
                        text: apiResponse.errorMessage || 'No data'
                    });
                    return;
                }
            }
            // Create a map of all available tags by id
            order.value.tags = order.value.tags.filter(tag => selectedTagIds.includes(tag.id));
            // Build the final tag list based on selectedTagIds order
            selectedTagIds.forEach(tagId => {
                const exists = order.value.tags.some(tag => tag.id === tagId);
                if (!exists) {
                    const fullTag = orderTags.value.find(tag => tag.id === tagId);
                    if (fullTag) {
                        order.value.tags.push(fullTag);
                    }
                }
            });
            addTagsModal.value?.close();
            swal.close();
        }

        async function getOrderDetailsAfterChangeStatus() {
            const apiResult = await api.GetOrder(props.id);
            if (apiResult.data?.error) {
                swal.fire({
                    icon: 'error',
                    text: apiResult.data?.error
                });
                return;
            }
            order.value = apiResult.data?.order || new OrderDetails();
            getMessages();
        }

        async function createReceipt() {
            const noBalanceToPay = !!order.value.businessClientId && order.value.ballance == 0;
            if (noBalanceToPay) {
                const swalAction = await swal.fire({
                    icon: 'info',
                    text: t('alert.order.no-balance-to-pay'),
                    customClass: {
                        confirmButton: 'save-button-wrapper popup-bookit-button my-1 px-5',
                        cancelButton: 'close-button-wrapper popup-bookit-button my-1 px-5'
                    },
                    buttonsStyling: false,
                    showCancelButton: true,
                    confirmButtonText: t('button.yes'),
                    cancelButtonText: t('button.no')
                });
                if (!swalAction.isConfirmed) {
                    return;
                }
            }
            router.push({ name: 'receiptNew', params: { orderId: props.id } });
        }

        function hasNoBalanceToInvoice(): boolean {
            return !!order.value.businessClientId && order.value.totalToPay - order.value.invoiceTotal === 0;
        }

        async function createInvoice() {
            const noBalanceToInvoice = hasNoBalanceToInvoice();
            if (noBalanceToInvoice) {
                const swalAction = await swal.fire({
                    icon: 'info',
                    text: t('alert.order.no-balance-to-invoice'),
                    customClass: {
                        confirmButton: 'save-button-wrapper popup-bookit-button my-1 px-5',
                        cancelButton: 'close-button-wrapper popup-bookit-button my-1 px-5'
                    },
                    buttonsStyling: false,
                    showCancelButton: true,
                    confirmButtonText: t('button.yes'),
                    cancelButtonText: t('button.no')
                });
                if (!swalAction.isConfirmed) {
                    return;
                }
            }
            router.push({ name: 'newInvoice', params: { orderId: props.id } });
        }

        async function createPerfomaInvoice() {
            const noBalanceToInvoice = hasNoBalanceToInvoice();
            if (noBalanceToInvoice) {
                const swalAction = await swal.fire({
                    icon: 'info',
                    text: t('alert.order.no-balance-to-invoice'),
                    customClass: {
                        confirmButton: 'save-button-wrapper popup-bookit-button my-1 px-5',
                        cancelButton: 'close-button-wrapper popup-bookit-button my-1 px-5'
                    },
                    buttonsStyling: false,
                    showCancelButton: true,
                    confirmButtonText: t('button.yes'),
                    cancelButtonText: t('button.no')
                });
                if (!swalAction.isConfirmed) {
                    return;
                }
            }
            router.push({ name: 'newPerfomaInvoice', params: { orderId: props.id } });
        }

        onMounted(onMountedAction);

        return {
            getFullNameOrderStatus,
            order,
            removemillis,
            getMinutes,
            EditOrder,
            actions,
            changeRemarks,
            message,
            sendMsg,
            changeOrderProductDate,
            OrderStatusColors,
            formatChatDate,
            chatTimerId,
            filterType,
            changeChatType,
            getNextMessages,
            actionsMenu,
            isActionsExpand,
            isQuoteActionsExpand,
            changeStatus,
            messagedMarked,
            isArrivalConfirmationExpand,
            arrivalConfirmation,
            onSaveArrivalConfirmation,
            removeProductCustomer,
            customerEdit,
            saveOrderCustomer,
            addProduct,
            submitForm,
            back,
            channels,
            loaded,
            orderProductExist,
            priceTypeSelect,
            priceTypes,
            isGroupPrice,
            mobileNumberChanged,
            emailChanged,
            incorrectMobile,
            incorrectEmail,
            businessClientSelect,
            calculateOrder,
            creatingBlocked,
            clients,
            adjustTextareaHeight,
            unassignEmployee,
            assignEmployee,
            orderType,
            canChangeUnitPrice,
            onBussinessClientChange,
            debounce: createDebounce(),
            calculateCustomerRowHandler: calculateCustomerRowHandler,
            showContractFormModal,
            updateContractFormModalVisibility,
            contractFormModalConfirmed,
            goToCrmCustomerDetails,
            goToCrmCustomerLeadDetails,
            isQuote,
            showQuoteActions,
            onSelectQuoteAction,
            openTagsManagementModal,
            openAddTagsModal,
            removeTagFromOrder,
            addTagsModal,
            orderTags,
            onSaveTagsHandler,
            onSaveTag,
            tagsManagementModal,
            onDeleteTag,
            getOrderDetailsAfterChangeStatus,
            modalCrmCustomerChatMessage,
            openCrmCustomerChatMessageModal,
            createReceipt,
            createInvoice,
            createPerfomaInvoice,
	    goBack
        };
    }
});
